import * as AddressFormatter from '@shopify/address';
import axios, { AxiosError } from 'axios';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import Micromodal from 'micromodal';
import { useEffect, useLayoutEffect, useState } from 'react';
import { useParams } from 'react-router';
import { getModalOption, Setting, Subscription, SubscriptionLine } from '../App';
import { billingPolicyIntervals, CUSTOMER_API_URL } from '../constants';
import { calculateChangeDeadline } from '../utils/calculateChangeDeadline';

type DeliveryDate = {
  current: dayjs.Dayjs;
  change: dayjs.Dayjs;
  min: dayjs.Dayjs;
  max?: dayjs.Dayjs;
  time?: string;
  noDay: boolean;
};

type PlanPair = {
  lineId: string;
  planId: string;
};

type PageStatus = {
  removeAction: boolean;
  newOrderAction: boolean;
  quantityAction: boolean;
  planModifyAction: boolean;
};

type Plan = {
  planId: string;
  name: string;
  description: string;
  billingPolicyInterval: 'DAY' | 'WEEK' | 'MONTH' | 'YEAR';
  billingPolicyIntervalCount: number;
  pricingPolicyAdjustmentType: string | null;
  pricingPolicyAdjustmentValue: number | null;
};

type Box = {
  boxId: string;
  title: string;
  description: string;
  parentProductId: string;
};

type PlanGroup = {
  merchantCode: string;
  name: string;
  planGroupId: string;
  plans: Plan[];
  boxes: Box[];
};

type CancelSurveySetting = {
  enable: boolean;
  leadText?: string;
  require: boolean;
  extraField: boolean;
  reasons?: string[];
};

type CancelSurveyInput = {
  selected?: string;
  extraField?: string;
};

type getCancelSurveyResponse = {
  cancelSurveyEnable: boolean;
  cancelSurveyLeadText: string;
  cancelSurveyRequire: boolean;
  cancelSurveyExtraField: boolean;
  reasons: {
    text: string;
  }[];
};

type AllowModifyDay = {
  title: string;
  day: number;
  limitDate?: dayjs.Dayjs;
};

dayjs.extend(isSameOrAfter);

export const useSubscriptionDetail = (setting: Setting) => {
  const [subscription, setSubscription] = useState<Subscription>();
  const { id } = useParams<{ id: string }>();
  const [deliveryDate, setDeliveryDate] = useState<DeliveryDate>();
  const [subscriptionLinePlan, setSubscriptionLinePlan] = useState<PlanPair[]>([]);
  const [removeItem, setRemoveItem] = useState<SubscriptionLine>();
  const [quantityItem, setQuantityItem] = useState<SubscriptionLine>();
  const [pageStatus, setPageStatus] = useState<PageStatus>({
    removeAction: false,
    newOrderAction: false,
    quantityAction: false,
    planModifyAction: false,
  });
  const [skipCount, setSkipCount] = useState<string>('1');
  const [planGroups, setPlanGroups] = useState<PlanGroup[]>([]);
  const [modifyPlanId, setModifyPlanId] = useState<string>('');
  const [cancelSurveySetting, setCancelSurveySetting] = useState<CancelSurveySetting>({
    enable: false,
    require: false,
    extraField: false,
  });
  const [cancelSurveyInput, setCancelSurveyInput] = useState<CancelSurveyInput>({});
  const [allowModifyDay, setAllowModifyDay] = useState<AllowModifyDay>({
    title: '',
    day: 3,
  });

  // TODO: locale
  const addressFormatter = new AddressFormatter.default('ja');

  useEffect(() => {
    window.parent.postMessage({ action: 'subscriptionDetail' }, '*');

    axios
      .post(
        `${CUSTOMER_API_URL}?action=getSubscription`,
        { subscriptionId: id },
        {
          headers: { Authorization: setting.token },
        },
      )
      .then((response) => {
        setSubscription({ ...response.data, disabled: false });
      });

    axios
      .post(
        `${CUSTOMER_API_URL}?action=getSubscriptionLinePlan`,
        { subscriptionId: id },
        {
          headers: { Authorization: setting.token },
        },
      )
      .then((response) => {
        setSubscriptionLinePlan(response.data);
      });

    axios
      .post(
        `${CUSTOMER_API_URL}?action=getPlanGroups`,
        {},
        {
          headers: { Authorization: setting.token },
        },
      )
      .then((response) => {
        setPlanGroups(response.data);
      });

    axios
      .post(
        `${CUSTOMER_API_URL}?action=getCancelSurvey`,
        {},
        {
          headers: { Authorization: setting.token },
        },
      )
      .then((response) => {
        const data: getCancelSurveyResponse = response.data;
        setCancelSurveySetting({
          enable: data.cancelSurveyEnable,
          leadText: data.cancelSurveyLeadText,
          require: data.cancelSurveyRequire,
          extraField: data.cancelSurveyExtraField,
          reasons: data.reasons.map((reason) => {
            return reason.text;
          }),
        });
      });
  }, []);

  useEffect(() => {
    if (!subscription || subscription!.disabled) return;

    const limitDate = calculateChangeDeadline(subscription);
    setAllowModifyDay({
      ...allowModifyDay,
      day: subscription.shop.allowModifyDays,
      limitDate,
    });

    const nextBilling = dayjs(subscription.nextBillingDate);
    const purchaseAt = subscription!.billingAttempts.length
      ? subscription!.billingAttempts[0].createdAt
      : subscription!.createdAt;
    const nextDelivery = nextDeliveryDate(
      nextBilling,
      subscription!.deliveryDays === -1 ? subscription!.shop.deliveryDays : subscription!.deliveryDays,
    );
    const nextDeliveryLimit = nextDeliveryDate(nextDelivery, subscription!.shop!.deliveryDaysMax);
    const nextBillingLimit = nextBilling.add(
      subscription!.billingPolicyIntervalCount,
      subscription!.billingPolicyInterval.toLowerCase() as 'day' | 'week' | 'month',
    );

    let nextDeliveryMin: dayjs.Dayjs | undefined = nextBilling.add(subscription!.shop!.deliveryDays + 1, 'day');
    let nextDeliveryMax: dayjs.Dayjs | undefined = nextBilling.add(30, 'day');

    if (subscription!.shop!.changeNextBillingDateWithDeliveryDate) {
      nextDeliveryMax =
        subscription!.shop!.deliveryDaysMax === 0
          ? undefined
          : nextBillingLimit.isAfter(nextDeliveryLimit)
            ? nextDeliveryLimit
            : nextBillingLimit;
      nextDeliveryMin = dayjs()
        .add(subscription!.shop.deliveryDays, 'day')
        .isAfter(dayjs(purchaseAt).add(subscription!.shop!.deliveryDays, 'day'))
        ? dayjs().add(subscription!.shop.deliveryDays + 1, 'day')
        : dayjs(purchaseAt).add(subscription!.shop!.deliveryDays, 'day');
    }

    setDeliveryDate({
      current: nextDelivery,
      change: nextDelivery,
      min: nextDeliveryMin,
      max: nextDeliveryMax,
      time: subscription!.deliveryTime,
      noDay: subscription!.deliveryDays === -1 && subscription!.shop.enableDeliveryDateNoDay,
    });

    // MicroModal の初期化
    Micromodal.init();
  }, [subscription]);

  useEffect(() => {
    (async () => {
      if (!subscription) return;

      if (subscription.deliveryFormattedAddress && subscription.deliveryFormattedAddress.length !== 0) return;

      // Ignore if subscription's delivery address is blank
      if (!subscription.deliveryCountryCode) return;

      const {
        deliveryAddress1,
        deliveryAddress2,
        deliveryCity,
        deliveryCompany,
        deliveryCountryCode,
        deliveryFirstName,
        deliveryLastName,
        deliveryPhone,
        deliveryProvinceCode,
        deliveryZip,
      } = subscription;

      let deliveryFormattedAddress = await addressFormatter.format({
        address1: deliveryAddress1,
        address2: deliveryAddress2,
        city: deliveryCity,
        company: deliveryCompany,
        country: deliveryCountryCode,
        firstName: deliveryFirstName,
        lastName: deliveryLastName,
        phone: deliveryPhone,
        province: deliveryProvinceCode,
        zip: deliveryZip,
      });
      const countries = await addressFormatter.getCountries();
      const country = await addressFormatter.getCountry(deliveryCountryCode);

      //書式の変更が未定なのでコメントアウト
      //deliveryFormattedAddress = sortAddress(country.formatting.show, deliveryFormattedAddress);

      setSubscription({
        ...subscription,
        deliveryFormattedAddress,
        countries,
        country,
      });
    })();
  }, [subscription]);

  useLayoutEffect(() => {
    const height = document.querySelector('body')!.scrollHeight + 100; // buffer;

    setting.resize(height);
    setting.monitorScroll();
  }, [subscription]);

  const nextDeliveryDate = (nextBillingDate: dayjs.Dayjs, deliveryDays: number): dayjs.Dayjs => {
    let nextDelivery = nextBillingDate.add(deliveryDays, 'day');

    if (!disableWeek().every((v) => v)) {
      const disableDays = disableWeek().slice(nextDelivery.day()).concat(disableWeek().slice(0, nextDelivery.day()));
      nextDelivery = nextDelivery.add(
        disableDays.findIndex((v) => !v),
        'days',
      );
    }

    return nextDelivery;
  };

  const showAllowModifyDay = (title: any) => {
    if (subscription!.shop.isAllowModifyDays && allowModifyDay.limitDate && dayjs().isAfter(allowModifyDay.limitDate)) {
      setAllowModifyDay({
        ...allowModifyDay,
        title: title,
      });

      Micromodal.show('modal-allow-modify-day');
      return true;
    }

    return false;
  };

  const skip = () => {
    setSubscription({ ...subscription!, disabled: true });

    axios
      .post(
        `${CUSTOMER_API_URL}?action=skipSubscription`,
        {
          subscriptionId: id,
          skipCount,
          skipDate: deliveryDate ? deliveryDate.current.format('YYYY年M月D日') : '',
        },
        {
          headers: { Authorization: setting.token },
        },
      )
      .then((response) => {
        const nextBilling = new Date(response.data.nextBillingDate);
        setSubscription({
          ...subscription!,
          nextBillingDate: nextBilling,
          disabled: false,
          subscriptionHistories: response.data.subscriptionHistories,
        });

        Micromodal.show('modal-skip-complete', getModalOption(setting));
      })
      .catch((error: AxiosError) => {
        if (error.response!.status >= 500) {
          throw error;
        }
      });

    Micromodal.close('modal-skip');
  };

  const skipCancel = () => {
    setSubscription({ ...subscription!, disabled: true });

    axios
      .post(
        `${CUSTOMER_API_URL}?action=skipCancelSubscription`,
        {
          subscriptionId: id,
          skipCancelDate: deliveryDate ? deliveryDate.current.format('YYYY年M月D日') : '',
        },
        {
          headers: { Authorization: setting.token },
        },
      )
      .then((response) => {
        const nextBilling = new Date(response.data.nextBillingDate);
        setSubscription({
          ...subscription!,
          nextBillingDate: nextBilling,
          disabled: false,
          subscriptionHistories: response.data.subscriptionHistories,
        });

        Micromodal.show('modal-skip-cancel-complete', getModalOption(setting));
      })
      .catch((error: AxiosError) => {
        if (error.response!.status >= 500) {
          throw error;
        }
      });

    Micromodal.close('modal-skip-cancel');
  };

  const updateStatusOpen = (status: string) => {
    const title: { [key: string]: string } = {
      skip: '定期購入のスキップ',
      paused: '定期購買の一時停止',
      cancelled: '定期購買の解約',
      active: '定期購買の再開',
      'skip-cancel': 'スキップの取り消し',
      'delivery-days': 'お届け予定日の変更',
    };

    if (showAllowModifyDay(title[status])) {
      return;
    }

    setDeliveryDate({
      ...deliveryDate!,
      change: deliveryDate!.current!,
    });
    Micromodal.show(`modal-${status}`, getModalOption(setting));
  };

  const updateStatus = (status: string) => {
    setSubscription({ ...subscription!, disabled: true });

    const param =
      status === 'cancelled'
        ? {
            subscriptionId: id,
            status,
            survey: cancelSurveyInput,
          }
        : {
            subscriptionId: id,
            status,
          };

    axios
      .post(`${CUSTOMER_API_URL}?action=updateSubscription`, param, {
        headers: { Authorization: setting.token },
      })
      .then((response) => {
        setSubscription({
          ...subscription!,
          status: response.data.status,
          disabled: false,
        });

        Micromodal.show(`modal-${status}-complete`, getModalOption(setting));
      });

    Micromodal.close(`modal-${status}`);
  };

  const disableWeek = () => {
    return [
      subscription!.shop.disableDeliveryDaysSun,
      subscription!.shop.disableDeliveryDaysMon,
      subscription!.shop.disableDeliveryDaysTue,
      subscription!.shop.disableDeliveryDaysWed,
      subscription!.shop.disableDeliveryDaysThr,
      subscription!.shop.disableDeliveryDaysFri,
      subscription!.shop.disableDeliveryDaysSat,
    ];
  };

  const nextDeliveryDateChange = (date: Date) => {
    setDeliveryDate({
      ...deliveryDate!,
      change: dayjs(date),
    });
  };

  const selectDeliveryTime = (selected: string) => {
    setDeliveryDate({
      ...deliveryDate!,
      time: selected,
    });
  };

  const isDisableDays = (date: Date) => {
    return !disableWeek()[date.getDay()];
  };

  const selectNoDay = (elm: HTMLInputElement) => {
    setDeliveryDate({
      ...deliveryDate!,
      noDay: elm.checked,
      time: elm.checked ? '' : subscription!.deliveryTimeText,
    });
  };

  const confirmDeliveryDays = () => {
    Micromodal.close('modal-delivery-days');

    if (deliveryDate!.noDay) {
      Micromodal.show('modal-delivery-days-confirm-noday', getModalOption(setting));
    } else {
      Micromodal.show('modal-delivery-days-confirm', getModalOption(setting));
    }
  };

  const updateDeliveryDays = () => {
    setSubscription({ ...subscription!, disabled: true });

    const changeDate = deliveryDate!.change;
    const nextBilling = changeDate.subtract(subscription!.shop!.deliveryDays, 'day');

    let deliveryDays: number;
    if (subscription!.shop!.changeNextBillingDateWithDeliveryDate) {
      deliveryDays = deliveryDate!.noDay ? -1 : subscription!.shop!.deliveryDays;
    } else {
      deliveryDays = deliveryDate!.change.diff(dayjs(subscription!.nextBillingDate), 'day');
    }

    axios
      .post(
        `${CUSTOMER_API_URL}?action=updateDeliveryDays`,
        {
          subscriptionId: id,
          deliveryDays: deliveryDays,
          nextBilling,
          deliveryTime: deliveryDate?.time,
          deliveryDate: deliveryDate ? deliveryDate.current.format('YYYY年M月D日') : '',
        },
        {
          headers: { Authorization: setting.token },
        },
      )
      .then((response) => {
        // 定期購買データを修正すると、お届け予定日も自動計算されるためここでは設定しない
        setSubscription({
          ...subscription!,
          ...response.data,
          nextBillingDate: new Date(response.data.nextBillingDate),
          disabled: false,
        });

        Micromodal.close('modal-delivery-days-confirm');
        Micromodal.close('modal-delivery-days-confirm-noday');
        Micromodal.show('modal-delivery-days-complete', getModalOption(setting));
      });
  };

  const openPaymentChange = () => {
    Micromodal.show('modal-payment-change', getModalOption(setting));
  };

  const paymentMethodUpdate = () => {
    axios
      .post(
        `${CUSTOMER_API_URL}?action=paymentMethodUpdate`,
        {
          subscriptionId: id,
        },
        {
          headers: { Authorization: setting.token },
        },
      )
      .then((response) => {
        Micromodal.close('modal-payment-change');
        Micromodal.show('modal-payment-change-complete', getModalOption(setting));
      })
      .catch((error) => {
        Micromodal.close('modal-payment-change');
        Micromodal.show('modal-error', getModalOption(setting));
      });
  };

  const onClickRemoveProduct = (line: SubscriptionLine) => {
    if (showAllowModifyDay('商品の削除')) {
      return;
    }

    setRemoveItem(line);
    Micromodal.show('modal-remove-product', getModalOption(setting));
  };

  const onClickRemoveAction = async () => {
    console.log('before return');
    if (!removeItem || !subscription) {
      return;
    }
    console.log('after return');

    setPageStatus({
      ...pageStatus,
      removeAction: true,
    });
    console.log('after setPageStatus');

    try {
      // データの削除
      const removeSubscriptionLine = await axios.post(
        `${CUSTOMER_API_URL}?action=removeSubscriptionLine`,
        {
          subscriptionId: id,
          subscriptionLine: removeItem!,
        },
        {
          headers: { Authorization: setting.token },
        },
      );

      console.log('before removeSubscriptionLine');
      if (removeSubscriptionLine) {
        setSubscription({
          ...subscription!,
          subscriptionLines: removeSubscriptionLine.data.subscriptionLines,
        });
      }
      console.log('after removeSubscriptionLine');
    } catch (e) {
      Micromodal.close('modal-remove-product');
      Micromodal.show('modal-error', getModalOption(setting));

      setPageStatus({
        ...pageStatus,
        removeAction: false,
      });
      return;
    }

    setPageStatus({
      ...pageStatus,
      removeAction: false,
    });

    Micromodal.close('modal-remove-product');
    Micromodal.show('modal-remove-product-complete', getModalOption(setting));
  };

  const openNewOrderModal = () => {
    Micromodal.show('modal-new-order', getModalOption(setting));
  };

  const onClickNewOrderAction = async () => {
    setPageStatus({
      ...pageStatus,
      newOrderAction: true,
    });

    axios
      .post(
        `${CUSTOMER_API_URL}?action=newOrder`,
        {
          subscriptionId: id,
        },
        {
          headers: { Authorization: setting.token },
        },
      )
      .then((response) => {
        setSubscription({
          ...subscription!,
          billingAttempts: [
            {
              orderId: response.data.orderId,
              orderToken: response.data.orderToken,
              orderName: response.data.orderName,
              ready: response.data.ready,
              createdAt: response.data.createdAt,
              errorMessage: response.data.errorMessage,
              errorCode: response.data.errorCode,
            },
            ...subscription!.billingAttempts,
          ],
        });

        Micromodal.close('modal-new-order');
        Micromodal.show('modal-new-order-complete', getModalOption(setting));
      })
      .catch((e) => {
        Micromodal.close('modal-new-order');
        Micromodal.show('modal-error', getModalOption(setting));

        setPageStatus({
          ...pageStatus,
          removeAction: false,
        });
        return;
      });

    setPageStatus({
      ...pageStatus,
      newOrderAction: false,
    });
  };

  const onClickQuantityModify = (line: SubscriptionLine) => {
    if (showAllowModifyDay('数量の変更')) {
      return;
    }

    setQuantityItem(line);
    Micromodal.show('modal-quantity-modify', getModalOption(setting));
  };

  const onChangeQuantity = (value: number) => {
    setQuantityItem({
      ...quantityItem!,
      quantity: value,
    });
  };

  const onClickQuantityAction = async () => {
    if (!quantityItem) {
      return;
    }

    if (subscription!.shop.showStockAlert) {
      try {
        const response = await axios.post(
          `${CUSTOMER_API_URL}?action=checkVariantSoldOut`,
          {
            variantId: quantityItem.variantId,
          },
          {
            headers: { Authorization: setting.token },
          },
        );

        if (response.data.soldOut) {
          Micromodal.close('modal-quantity-modify');
          Micromodal.show('modal-stock-error');

          return;
        }
      } catch (e) {
        console.log((e as Error).message);

        return;
      }
    }

    setPageStatus({
      ...pageStatus,
      quantityAction: true,
    });

    await axios
      .post(
        `${CUSTOMER_API_URL}?action=updateSubscriptionLine`,
        {
          subscriptionId: id,
          lineId: quantityItem!.lineId,
          quantity: quantityItem!.quantity,
        },
        {
          headers: { Authorization: setting.token },
        },
      )
      .then((response) => {
        setSubscription({
          ...subscription!,
          subscriptionLines: subscription!.subscriptionLines.map((line) => {
            if (line.lineId !== response.data.lineId) {
              return line;
            }

            return response.data;
          }),
        });
        setPageStatus({
          ...pageStatus,
          quantityAction: false,
        });

        // Micromodal.close('modal-quantity-modify');
        Micromodal.show(`modal-quantity-modify-complete-${quantityItem!.lineId}`, getModalOption(setting));
      })
      .catch((e) => {
        // Micromodal.close('modal-quantity-modify');
        console.log('エラー: ', e);
        Micromodal.show(`modal-error`, getModalOption(setting));

        setPageStatus({
          ...pageStatus,
          quantityAction: false,
        });
        return;
      });
  };

  // 独自数量変更アクション

  const onClickShokutasuQuantityAction = async ({
    variantId,
    subscriptionId,
    lineId,
    quantity,
  }: {
    variantId: string;
    subscriptionId: string;
    lineId: string;
    quantity: number;
  }) => {
    console.log('variantId', variantId);
    console.log('subscriptionId', subscriptionId);
    console.log('lineId', lineId);
    console.log('quantity', quantity);

    if (subscription!.shop.showStockAlert) {
      try {
        const response = await axios.post(
          `${CUSTOMER_API_URL}?action=checkVariantSoldOut`,
          {
            variantId: variantId,
          },
          {
            headers: { Authorization: setting.token },
          },
        );

        if (response.data.soldOut) {
          alert('在庫切れです。');
          // Micromodal.close('modal-quantity-modify');
          // Micromodal.show('modal-stock-error');

          return;
        }
      } catch (e) {
        console.log((e as Error).message);

        return;
      }
    }

    setPageStatus({
      ...pageStatus,
      quantityAction: true,
    });

    await axios
      .post(
        `${CUSTOMER_API_URL}?action=updateSubscriptionLine`,
        {
          subscriptionId: subscriptionId,
          lineId: lineId,
          quantity: quantity,
        },
        {
          headers: { Authorization: setting.token },
        },
      )
      .then((response) => {
        setSubscription({
          ...subscription!,
          subscriptionLines: subscription!.subscriptionLines.map((line) => {
            if (line.lineId !== response.data.lineId) {
              return line;
            }

            return response.data;
          }),
        });
        setPageStatus({
          ...pageStatus,
          quantityAction: false,
        });
        console.log('変更完了。');
        // Micromodal.close('modal-quantity-modify');
        Micromodal.show(`modal-quantity-modify-complete-${variantId}`, getModalOption(setting));
      })
      .catch((e) => {
        console.log(`modal-quantity-modify-complete-${variantId}`, variantId);
        console.log('なんらかのエラー:', e);
        // Micromodal.close('modal-quantity-modify');
        Micromodal.show('modal-error', getModalOption(setting));

        setPageStatus({
          ...pageStatus,
          quantityAction: false,
        });
        return;
      });
  };

  const openPlanModifyModal = () => {
    if (showAllowModifyDay('お届け周期の変更')) {
      return;
    }

    if (!planGroups || planGroups.length <= 0) {
      return;
    }
    const planList = getPlanModifyList();
    if (planList!.length >= 1) {
      setModifyPlanId(planList![0].planId);
    }

    Micromodal.show('modal-plan-modify', getModalOption(setting));
  };

  const onChangePlanModify = (e: any) => {
    setModifyPlanId(e.target.value);
  };

  const getPlanModifyList = () => {
    if (!planGroups || planGroups.length <= 0 || !subscription) {
      return;
    }

    // 設定されている商品バリエーションを確認
    const planGroupsFilter = planGroups
      .filter((planGroup: PlanGroup) => {
        // 同一プランが含まれるか確認
        const plan = planGroup!.plans.find((plan) => {
          return (
            plan.billingPolicyInterval === subscription.billingPolicyInterval &&
            plan.billingPolicyIntervalCount === subscription.billingPolicyIntervalCount
          );
        });
        if (!plan) {
          return false;
        }

        // SubscriptionLineのsellingPlanIdがPlanGroupのPlanのいずれかに一致する
        return subscription.subscriptionLines.some((line) =>
          planGroup.plans.some((plan) => plan.planId === line.sellingPlanId),
        );
      })
      .map((planGroup: PlanGroup) => {
        return planGroup.plans;
      });

    let samePlans: Plan[] = [];
    if (planGroupsFilter.length <= 1) {
      samePlans = planGroupsFilter.flat();
    } else {
      // 全てのプランで、重複しているプランを抽出
      let plans = planGroupsFilter.flat();
      while (plans.length) {
        const target = plans.shift();
        const check = plans.filter((plan) => {
          return (
            plan.billingPolicyIntervalCount === target!.billingPolicyIntervalCount &&
            plan.billingPolicyInterval === target!.billingPolicyInterval
          );
        });

        if (check.length) {
          samePlans = samePlans.concat([target!]);
          plans = plans.filter(
            (plan) =>
              plan.billingPolicyIntervalCount !== target!.billingPolicyIntervalCount ||
              plan.billingPolicyInterval !== target!.billingPolicyInterval,
          );
        }
      }
    }

    return samePlans.filter((plan) => {
      // 同一のプランは除外する
      return (
        plan.billingPolicyInterval !== subscription!.billingPolicyInterval ||
        plan.billingPolicyIntervalCount !== subscription!.billingPolicyIntervalCount
      );
    });
  };

  const planModifyAction = async () => {
    if (!planGroups || !planGroups.length || modifyPlanId === '' || !subscription) {
      return;
    }

    setPageStatus({
      ...pageStatus,
      planModifyAction: true,
    });

    // 該当する商品のプランに合わせてPlanIDを設定する
    const targetPlan = planGroups
      .map((planGroup) => {
        return planGroup.plans;
      })
      .flat()
      .filter((plan) => plan.planId === modifyPlanId)[0];

    await axios
      .post(
        `${CUSTOMER_API_URL}?action=updateSubscriptionLinePlan`,
        {
          subscriptionId: id,
          subscriptionLines: subscription.subscriptionLines.map((line) => ({
            planId: targetPlan.planId,
            planName: targetPlan.name,
            lineId: line.lineId,
            variantId: line.variantId,
            pricingPolicyAdjustmentType: targetPlan.pricingPolicyAdjustmentType,
            pricingPolicyAdjustmentValue: targetPlan.pricingPolicyAdjustmentValue,
          })),
          billingPolicyInterval: targetPlan.billingPolicyInterval,
          billingPolicyIntervalCount: targetPlan.billingPolicyIntervalCount,
        },
        {
          headers: { Authorization: setting.token },
        },
      )
      .then((response) => {
        setSubscription({
          ...subscription!,
          ...response.data,
        });

        setPageStatus({
          ...pageStatus,
          planModifyAction: false,
        });

        Micromodal.close('modal-plan-modify');
        Micromodal.show('modal-plan-modify-complete', getModalOption(setting));
      })
      .catch((e) => {
        Micromodal.close('modal-plan-modify');
        Micromodal.show('modal-error', getModalOption(setting));

        setPageStatus({
          ...pageStatus,
          planModifyAction: false,
        });
      });
  };

  const getPlanModifyName = () => {
    if (!planGroups || !planGroups.length) {
      return '';
    }

    return planGroups
      .map((planGroup) => {
        return planGroup.plans;
      })
      .flat()
      .find((plan) => plan.planId === modifyPlanId)?.name;
  };

  const getPlanModifyCycle = () => {
    if (!planGroups || !planGroups.length) {
      return '';
    }

    const plan = planGroups
      .map((planGroup) => {
        return planGroup.plans;
      })
      .flat()
      .find((plan) => plan.planId === modifyPlanId);

    if (!plan?.billingPolicyIntervalCount || !plan?.billingPolicyInterval) return '';
    return plan.billingPolicyIntervalCount + billingPolicyIntervals[plan.billingPolicyInterval];
  };

  // subscriptionの更新
  // ================================================
  const updateSubscription = async () => {
    if (!subscription) return;
    try {
      const response = await axios.post(
        `${CUSTOMER_API_URL}?action=getSubscription`,
        { subscriptionId: subscription.subscriptionContractId },
        {
          headers: { Authorization: setting.token },
        },
      );
      setSubscription({ ...response.data, disabled: false });
    } catch (error) {
      console.error('Error updating subscription:', error);
    }
  };

  // subscriptionLineの楽観的更新（API再取得せず、ローカルstateで直接差し替え）
  const updateSubscriptionLineInState = (lineId: string, updatedLine: SubscriptionLine) => {
    setSubscription((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        subscriptionLines: prev.subscriptionLines.map((line) => (line.lineId === lineId ? updatedLine : line)),
      };
    });
  };

  const removeSubscriptionLineFromState = (lineId: string) => {
    setSubscription((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        subscriptionLines: prev.subscriptionLines.filter((line) => line.lineId !== lineId),
      };
    });
  };

  return {
    subscription,
    skip,
    skipCancel,
    updateStatus,
    deliveryDate,
    nextDeliveryDateChange,
    selectDeliveryTime,
    isDisableDays,
    updateDeliveryDays,
    updateStatusOpen,
    subscriptionLinePlan,
    paymentMethodUpdate,
    removeItem,
    onClickRemoveProduct,
    onClickRemoveAction,
    pageStatus,
    skipCount,
    setSkipCount,
    openNewOrderModal,
    onClickNewOrderAction,
    onClickQuantityModify,
    onClickQuantityAction,
    onChangeQuantity,
    quantityItem,
    openPlanModifyModal,
    onChangePlanModify,
    planModifyAction,
    planGroups,
    setPlanGroups,
    getPlanModifyList,
    getPlanModifyName,
    getPlanModifyCycle,
    cancelSurveySetting,
    cancelSurveyInput,
    setCancelSurveyInput,
    openPaymentChange,
    allowModifyDay,
    selectNoDay,
    confirmDeliveryDays,
    onClickShokutasuQuantityAction,
    updateSubscription,
    updateSubscriptionLineInState,
    removeSubscriptionLineFromState,
  };
};
